/* Bernard James — right-anchored DS drawer driver (BJ-DS, Figma "Shopify
   Final" 772:10566 / 772:10620 filter drawer; 868:76614 shop-the-look
   drawer).

   One base class, BjDrawer, owns everything a DS slide-over needs to behave:
   open/close with the one-frame transform dance, the scrim, body scroll
   lock, the focus trap, Escape, and focus return to the trigger. Two
   elements ride on it (ds_plan Part 5 §5 "reuse, don't duplicate"):

     · <bj-filter-drawer> — the collection filter drawer. Everything
       filter-specific (the APPLY (n) count preview, the batch-apply form
       wiring, the aria-controls trigger lookup) lives in the subclass, so
       its behaviour is byte-for-byte what it was before the base class was
       extracted.
     · <bj-look-drawer>  — the shop-the-look drawer
       (snippets/bj-shop-look-drawer.liquid). It has no form and no count;
       the base class is the whole behaviour. Its triggers render inside the
       collection grid markup this file does not own, so they are picked up
       by event delegation on document: any click on
       [data-bj-look-open="<drawer id>"] opens that drawer, and the clicked
       element becomes the focus-return target.

   The whole file is wrapped in an IIFE guarded on the first custom-element
   name: both snippets/bj-filter-trigger.liquid and
   snippets/bj-shop-look-drawer.liquid emit a <script> tag for this file, and
   when both render on one page (a collection with filters AND an editorial
   shop-the-look insert) the classic-script re-execution would otherwise
   throw on the top-level const redeclarations.

   ── The filter drawer's `APPLY (n)` count ─────────────────────────────────
   This file owns opening, closing, the focus trap, the aria wiring and the
   APPLY button's live result count. Filtering itself is the theme's existing
   AJAX facet machinery in facets.js: the drawer's <facet-filters-form> carries
   `data-batch-apply`, which makes that file listen for the form's `submit`
   event instead of binding its stock per-input debounce, so the grid
   re-fetches exactly once when APPLY is pressed (ds_plan §7.3). Nothing here
   renders the grid.

   772:10620 puts the result count on the button label, and the batch model
   means it has to be right for the PENDING selection, before anything is
   applied. Two mechanisms were on the table (ds_plan Part 5 §2.5):

   1. Derive it from the Shopify facet counts already in the drawer markup.
      REJECTED — measured on this store, those counts cannot produce the
      number. They are disjunctive: each value is counted with its own group's
      filters excluded, and the values are not disjoint. Colour Yellow Gold
      reports 3 and White Gold reports 3, but selecting both returns 3, not 6;
      In stock 253 + Out of stock 31 is 284 against a true total of 274. A sum
      is wrong within a single group, and an intersection across two groups is
      not derivable from the counts at all.

   2. A `section_id` fetch whose response is read for the count only. CHOSEN.
      It is exactly what the server would answer, and it is the one number the
      storefront can give. The response is consumed with a single regex for
      `#ProductCount`; the grid DOM is never touched, no <img> is created, no
      URL is pushed, and facets.js's own render path is never entered.

   The fetch is debounced 300ms so a burst of checkbox clicks coalesces, the
   previous request is aborted when a newer one supersedes it, and every answer
   is memoised by parameter string so re-checking a box costs nothing. `sort_by`
   is stripped from the preview parameters — it cannot change a result count,
   and dropping it keeps the memo warm across sort changes.

   Deliberately NOT written into facets.js's own `filterData` cache: sharing it
   would make APPLY serve the grid from the preview's response and issue zero
   requests, which would quietly break WS2's measured invariant that APPLY
   performs exactly one grid fetch. The redundant request is the price of
   keeping that invariant legible.

   Without JavaScript the filter drawer never opens, but the form is a plain
   GET form whose APPLY button is a real submit — so the no-JS path is a normal
   page navigation to the filtered URL, exactly as the stock <noscript>
   fallback behaved. The count span is hidden by `html.no-js` there, leaving a
   plain APPLY. The look drawer's no-JS path is the trigger's own fallback
   (WS-EDITORIAL renders the trigger; a plain navigation to the linked
   collection is the natural degrade). */

(() => {
	if (customElements.get('bj-filter-drawer')) return;

	/* Long enough that click-click-click on three checkboxes is one request,
	   short enough that the number feels attached to the click. */
	const BJ_COUNT_DEBOUNCE = 300;

	const BJ_FOCUSABLE = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'summary',
		'[tabindex]:not([tabindex="-1"])',
	].join(',');

	/* ------------------------------------------------------------------ */
	/* Base: any right-anchored DS drawer.                                  */
	/* ------------------------------------------------------------------ */

	class BjDrawer extends HTMLElement {
		constructor() {
			super();
			this.onKeydown = this.onKeydown.bind(this);
			this.open = this.open.bind(this);
			this.close = this.close.bind(this);
		}

		/* Subclass hooks — selectors only, never behaviour. The defaults are
		   the generic data-attribute vocabulary new drawers should use; the
		   filter drawer overrides them to its pre-existing class names so its
		   markup did not have to change when the base class was extracted. */
		get panelSelector() {
			return '[data-bj-drawer-panel]';
		}
		get closeSelector() {
			return '[data-bj-drawer-close]';
		}
		get firstFocusSelector() {
			return '[data-bj-drawer-first-focus]';
		}

		connectedCallback() {
			this.panel = this.querySelector(this.panelSelector);

			this.querySelectorAll(this.closeSelector).forEach((el) => {
				el.addEventListener('click', this.close);
			});

			this.addEventListener('keydown', this.onKeydown);
		}

		disconnectedCallback() {
			this.removeEventListener('keydown', this.onKeydown);
			document.body.classList.remove('overflow-hidden');
		}

		get isOpen() {
			return !this.hasAttribute('hidden');
		}

		open() {
			if (this.isOpen) return;

			this.removeAttribute('hidden');
			// One frame between display and the transform so the slide-in
			// animates rather than snapping.
			requestAnimationFrame(() => this.classList.add('is-open'));

			document.body.classList.add('overflow-hidden');
			if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

			const first =
				this.querySelector(this.firstFocusSelector) || this.focusable()[0];
			if (first) first.focus();
		}

		/* For delegated triggers (the look drawer): remember who opened the
		   drawer so close() can hand focus back to it. */
		openFrom(trigger) {
			if (trigger) this.trigger = trigger;
			this.open();
		}

		close() {
			if (!this.isOpen) return;

			this.classList.remove('is-open');
			document.body.classList.remove('overflow-hidden');
			if (this.trigger) {
				this.trigger.setAttribute('aria-expanded', 'false');
				this.trigger.focus();
			}

			const finish = () => this.setAttribute('hidden', '');
			const motionOk = window.matchMedia(
				'(prefers-reduced-motion: no-preference)'
			).matches;

			if (!motionOk || !this.panel) {
				finish();
				return;
			}

			// Wait for the slide-out before hiding, but never hang on a
			// transitionend that does not arrive.
			let done = false;
			const once = () => {
				if (done) return;
				done = true;
				this.panel.removeEventListener('transitionend', once);
				finish();
			};
			this.panel.addEventListener('transitionend', once);
			setTimeout(once, 500);
		}

		focusable() {
			return Array.from(this.querySelectorAll(BJ_FOCUSABLE)).filter(
				(el) => el.offsetParent !== null || el === document.activeElement
			);
		}

		onKeydown(event) {
			if (event.key === 'Escape') {
				event.preventDefault();
				this.close();
				return;
			}

			if (event.key !== 'Tab') return;

			const items = this.focusable();
			if (items.length === 0) return;

			const first = items[0];
			const last = items[items.length - 1];

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}
	}

	/* ------------------------------------------------------------------ */
	/* Filter drawer — base behaviour + the APPLY (n) count preview.        */
	/* ------------------------------------------------------------------ */

	class BjFilterDrawer extends BjDrawer {
		constructor() {
			super();
			this.queueCount = this.queueCount.bind(this);

			this.countCache = new Map();
			this.countTimer = null;
			this.countRequest = null;
		}

		get panelSelector() {
			return '.bj-filter-drawer__panel';
		}
		get closeSelector() {
			return '[data-bj-filter-close]';
		}
		get firstFocusSelector() {
			return '.bj-filter-drawer__close';
		}

		connectedCallback() {
			super.connectedCallback();

			this.form = this.querySelector('form');

			// The trigger lives in the filter bar, outside this element, and
			// points here with aria-controls.
			this.trigger = document.querySelector(
				`[data-bj-filter-open][aria-controls="${this.id}"]`
			);

			if (this.trigger) {
				this.trigger.addEventListener('click', this.open);
				this.trigger.setAttribute('aria-expanded', 'false');
			}

			// APPLY: facets.js re-fetches the grid; the drawer's only job is to
			// get out of the way and hand focus back.
			if (this.form) {
				this.form.addEventListener('submit', () => {
					// The preview is moot the moment the real fetch starts, and
					// an in-flight one would otherwise overwrite the applied
					// count.
					this.cancelCount();
					this.close();
				});
			}

			this.setupCount();
		}

		disconnectedCallback() {
			super.disconnectedCallback();
			if (this.trigger) this.trigger.removeEventListener('click', this.open);
			this.cancelCount();
		}

		/* -------------------------------------------------------------- */
		/* APPLY (n) — see the header note for why this is a fetch.        */
		/* -------------------------------------------------------------- */

		setupCount() {
			this.countBox = this.querySelector('[data-bj-apply-count]');
			this.countTarget = this.querySelector('#ProductCount');

			// The same section facets.js renders from, read the same way, so
			// the preview and the eventual apply can never disagree about the
			// target.
			const grid = document.getElementById('product-grid');
			this.countSection = grid ? grid.dataset.id : null;

			if (!this.form || !this.countBox || !this.countTarget || !this.countSection) {
				return;
			}

			// The server already rendered the count for the applied state, so
			// seed the memo with it: opening the drawer and closing it again is
			// free.
			this.countCache.set(this.countParams(), this.countTarget.textContent.trim());

			this.form.addEventListener('change', this.queueCount);
		}

		// Everything the form holds except sort_by, which cannot change a count.
		countParams() {
			const params = new URLSearchParams(new FormData(this.form));
			params.delete('sort_by');
			return params.toString();
		}

		cancelCount() {
			clearTimeout(this.countTimer);
			this.countTimer = null;
			if (this.countRequest) {
				this.countRequest.abort();
				this.countRequest = null;
			}
			if (this.countBox) delete this.countBox.dataset.bjLoading;
		}

		queueCount() {
			clearTimeout(this.countTimer);
			this.countTimer = setTimeout(() => this.refreshCount(), BJ_COUNT_DEBOUNCE);
		}

		refreshCount() {
			const params = this.countParams();

			// The memo, not a "what is displayed" flag, is the guard. facets.js
			// rewrites #ProductCount itself on APPLY and on CLEAR ALL, so any
			// flag this element kept would go stale behind its back — and
			// re-selecting a combination it had already previewed would then
			// show a number from the wrong state. A cache hit is cheap and
			// always right.
			if (this.countCache.has(params)) {
				this.countTarget.textContent = this.countCache.get(params);
				return;
			}

			if (this.countRequest) this.countRequest.abort();
			const request = new AbortController();
			this.countRequest = request;
			this.countBox.dataset.bjLoading = '';

			const url = `${window.location.pathname}?section_id=${this.countSection}&${params}`;

			fetch(url, { signal: request.signal })
				.then((response) => response.text())
				.then((text) => {
					// Deliberately a regex and not DOMParser: the section is
					// ~500KB of grid markup and only one number in it is wanted.
					// Nothing is inserted into the document.
					const found = text.match(/id=["']ProductCount["'][^>]*>([\s\S]*?)</);
					if (!found) return;

					const value = found[1].trim();
					this.countCache.set(params, value);

					// Only paint if the form still says what it said when this
					// request went out — an aborted-but-resolved response must
					// not overwrite a newer selection.
					if (this.countParams() === params) {
						this.countTarget.textContent = value;
					}
				})
				.catch(() => {
					// Aborted or offline. The label keeps the last known-good
					// count rather than flashing a guess.
				})
				.finally(() => {
					if (this.countRequest !== request) return;
					this.countRequest = null;
					delete this.countBox.dataset.bjLoading;
				});
		}
	}

	/* ------------------------------------------------------------------ */
	/* Shop-the-look drawer — the base behaviour is the whole behaviour.    */
	/* ------------------------------------------------------------------ */

	class BjLookDrawer extends BjDrawer {}

	/* The look drawer's triggers render inside the editorial inserts in the
	   collection grid — markup owned elsewhere and re-rendered by facets.js on
	   every filter apply, so direct binding would go stale. Delegation on
	   document survives both. Contract: the trigger carries
	   `data-bj-look-open="<drawer id>"`. */
	document.addEventListener('click', (event) => {
		const trigger = event.target.closest('[data-bj-look-open]');
		if (!trigger) return;

		const drawer = document.getElementById(
			trigger.getAttribute('data-bj-look-open')
		);
		if (!(drawer instanceof BjLookDrawer)) return;

		event.preventDefault();
		drawer.openFrom(trigger);
	});

	customElements.define('bj-filter-drawer', BjFilterDrawer);
	customElements.define('bj-look-drawer', BjLookDrawer);
})();
